import type { ServiceDraft } from "./services";

/**
 * Unpublished expansion catalogue from the approved website brief.
 *
 * These records intentionally contain only durable catalogue facts. The service
 * hydrator supplies safe page defaults. Set an individual record to
 * `enabled: true` only after Revolin approves its copy, imagery, localized
 * route slugs and publication scope.
 */
export const newServiceDrafts: readonly ServiceDraft[] = [
  {
    slug: "commercial-roofing",
    title: "Commercial Roofing",
    category: "commercial-strata",
    description:
      "Assessment-led commercial roofing for occupied buildings, capital projects and long-term asset planning across British Columbia.",
    customerTypes: ["commercial", "property-managers"],
    roofSystems: ["SBS", "TPO", "PVC", "metal"],
    benefits: [
      "Scope matched to the building",
      "Planned site coordination",
      "Clear lifecycle recommendations",
    ],
  },
  {
    slug: "commercial-roof-repair",
    title: "Commercial Roof Repair",
    category: "commercial-strata",
    description:
      "Targeted commercial roof investigation and repair with practical documentation for owners and property managers.",
    customerTypes: ["commercial", "property-managers"],
    roofSystems: ["SBS", "TPO", "PVC", "metal"],
    relatedServiceSlugs: ["flat-low-slope-roof-repair", "commercial-roofing"],
  },
  {
    slug: "commercial-roof-replacement",
    title: "Commercial Roof Replacement",
    category: "commercial-strata",
    description:
      "Planned replacement of aging commercial roof assemblies with staging built around access, occupants and operations.",
    customerTypes: ["commercial", "property-managers"],
    roofSystems: ["SBS", "TPO", "PVC", "metal"],
    relatedServiceSlugs: ["flat-roof-replacement-sbs-tpo-pvc", "commercial-roofing"],
  },
  {
    slug: "commercial-flat-roofing",
    title: "Commercial Flat Roofing",
    category: "commercial-strata",
    description:
      "Low-slope commercial roofing planned around drainage, penetrations, insulation and rooftop equipment.",
    customerTypes: ["commercial", "strata", "property-managers"],
    roofSystems: ["SBS", "TPO", "PVC", "EPDM"],
    image: "flat",
    relatedServiceSlugs: ["flat-roof-replacement-sbs-tpo-pvc", "flat-low-slope-roof-repair"],
  },
  {
    slug: "commercial-roof-inspection",
    title: "Commercial Roof Inspection",
    category: "commercial-strata",
    description:
      "Documented commercial roof condition assessments that support maintenance, budgeting and repair decisions.",
    customerTypes: ["commercial", "property-managers"],
    roofSystems: ["all roof systems"],
    relatedServiceSlugs: ["roof-health-check-maintenance", "commercial-preventive-maintenance"],
  },
  {
    slug: "commercial-preventive-maintenance",
    title: "Commercial Preventive Maintenance",
    category: "commercial-strata",
    description:
      "Scheduled commercial roof care focused on drainage, vulnerable details and early intervention before damage spreads.",
    customerTypes: ["commercial", "property-managers"],
    roofSystems: ["all roof systems"],
    relatedServiceSlugs: ["commercial-roof-inspection", "roof-health-check-maintenance"],
  },
  {
    slug: "strata-roofing",
    title: "Strata Roofing",
    category: "commercial-strata",
    description:
      "Roofing assessment, planning and project delivery for strata councils, managers and multi-owner communities.",
    customerTypes: ["strata", "property-managers"],
    roofSystems: ["shingles", "cedar", "metal", "low-slope membranes"],
  },
  {
    slug: "strata-roof-repair",
    title: "Strata Roof Repair",
    category: "commercial-strata",
    description:
      "Source-focused repairs for strata roof leaks and localized failures, supported by clear findings and next steps.",
    customerTypes: ["strata", "property-managers"],
    roofSystems: ["shingles", "cedar", "metal", "low-slope membranes"],
    relatedServiceSlugs: ["emergency-roof-leak-repair", "strata-roofing"],
  },
  {
    slug: "strata-roof-replacement",
    title: "Strata Roof Replacement",
    category: "commercial-strata",
    description:
      "Coordinated strata roof replacement with defined scope, resident communication considerations and phased planning.",
    customerTypes: ["strata", "property-managers"],
    roofSystems: ["shingles", "cedar", "metal", "low-slope membranes"],
    relatedServiceSlugs: ["strata-roofing"],
  },
  {
    slug: "strata-preventive-maintenance",
    title: "Strata Preventive Maintenance",
    category: "commercial-strata",
    description:
      "Repeatable strata roof maintenance that helps councils monitor condition and plan future capital work.",
    customerTypes: ["strata", "property-managers"],
    roofSystems: ["all roof systems"],
    relatedServiceSlugs: ["strata-roofing", "roof-health-check-maintenance"],
  },
  {
    slug: "multi-family-roofing",
    title: "Multi-Family Roofing",
    category: "commercial-strata",
    description:
      "Roofing services for townhomes, apartments and multi-family properties with occupied-site planning built into the scope.",
    customerTypes: ["multi-family", "strata", "property-managers"],
    roofSystems: ["shingles", "metal", "low-slope membranes"],
  },
  {
    slug: "property-manager-roofing-services",
    title: "Property Manager Roofing Services",
    shortTitle: "Property Manager Roofing",
    category: "commercial-strata",
    description:
      "Responsive assessment, documentation and planned roofing support for property management portfolios.",
    customerTypes: ["property-managers", "commercial", "strata"],
    roofSystems: ["all roof systems"],
  },
  {
    slug: "new-roof-installation",
    title: "New Roof Installation",
    category: "installation",
    description:
      "Complete roof assembly installation for additions and new buildings, coordinated with the project schedule and envelope details.",
    customerTypes: ["homeowners", "builders", "commercial"],
    roofSystems: ["shingles", "cedar", "metal", "low-slope membranes"],
  },
  {
    slug: "new-construction-roofing",
    title: "New Construction Roofing",
    category: "installation",
    description:
      "Roofing for new residential and commercial construction with attention to sequencing, specifications and weather protection.",
    customerTypes: ["builders", "commercial", "homeowners"],
    roofSystems: ["shingles", "cedar", "metal", "low-slope membranes"],
    relatedServiceSlugs: ["new-roof-installation"],
  },
  {
    slug: "skylight-installation",
    title: "Skylight Installation",
    category: "installation",
    description:
      "New skylight installation with curb, underlayment and flashing details integrated into the roof assembly.",
    customerTypes: ["homeowners", "builders"],
    roofSystems: ["shingles", "cedar", "metal", "low-slope membranes"],
    relatedServiceSlugs: ["skylight-leak-flashing-repair"],
  },
  {
    slug: "skylight-replacement",
    title: "Skylight Replacement",
    category: "installation",
    description:
      "Replacement of aging skylights with renewed flashing and compatible integration into the surrounding roof.",
    customerTypes: ["homeowners", "commercial", "strata"],
    roofSystems: ["shingles", "cedar", "metal", "low-slope membranes"],
    relatedServiceSlugs: ["skylight-leak-flashing-repair", "skylight-installation"],
  },
  {
    slug: "roof-conversion-services",
    title: "Roof Conversion Services",
    category: "roof-conversions",
    description:
      "Planned conversion from one roof covering to another after reviewing structure, ventilation, detailing and design goals.",
    customerTypes: ["homeowners", "commercial", "strata"],
    roofSystems: ["shingles", "cedar", "metal"],
  },
  {
    slug: "cedar-to-asphalt-roof-conversion",
    title: "Cedar-to-Asphalt Roof Conversion",
    shortTitle: "Cedar-to-Asphalt Conversion",
    category: "roof-conversions",
    description:
      "Convert an aging cedar roof to architectural asphalt shingles with deck, ventilation and flashing requirements assessed first.",
    customerTypes: ["homeowners", "strata"],
    roofSystems: ["cedar", "asphalt shingles"],
    image: "shingles",
    relatedServiceSlugs: ["cedar-shake-roof-replacement", "asphalt-shingle-replacement"],
  },
  {
    slug: "cedar-to-metal-roof-conversion",
    title: "Cedar-to-Metal Roof Conversion",
    shortTitle: "Cedar-to-Metal Conversion",
    category: "roof-conversions",
    description:
      "Convert a cedar roof to a metal system with assembly weight, substrate, ventilation and complex flashing details considered.",
    customerTypes: ["homeowners", "strata"],
    roofSystems: ["cedar", "metal"],
    image: "metal",
    relatedServiceSlugs: ["cedar-shake-roof-replacement", "metal-roof-replacement"],
  },
  {
    slug: "epdm-roofing",
    title: "EPDM Roofing",
    category: "roof-systems",
    description:
      "EPDM single-ply membrane roofing for suitable low-slope applications, specified around substrate, drainage and detailing needs.",
    customerTypes: ["commercial", "strata", "property-managers"],
    roofSystems: ["EPDM"],
    image: "flat",
    relatedServiceSlugs: ["flat-roof-replacement-sbs-tpo-pvc"],
  },
  {
    slug: "built-up-roofing",
    title: "Built-Up Roofing",
    category: "roof-systems",
    description:
      "Multi-layer built-up roofing assessment, repair and replacement planning for compatible low-slope buildings.",
    customerTypes: ["commercial", "strata", "property-managers"],
    roofSystems: ["built-up roofing"],
    image: "flat",
  },
  {
    slug: "tar-and-gravel-roofing",
    title: "Tar-and-Gravel Roofing",
    category: "roof-systems",
    description:
      "Condition assessment and planned work for traditional tar-and-gravel low-slope roof assemblies.",
    customerTypes: ["commercial", "strata", "property-managers"],
    roofSystems: ["tar and gravel", "built-up roofing"],
    image: "flat",
    relatedServiceSlugs: ["built-up-roofing"],
  },
  {
    slug: "roof-coatings-restoration",
    title: "Roof Coatings and Restoration",
    category: "roof-systems",
    description:
      "Specified roof coating and restoration options for eligible assemblies after adhesion, moisture and substrate review.",
    customerTypes: ["commercial", "strata", "property-managers"],
    roofSystems: ["coatings", "metal", "low-slope membranes"],
  },
  {
    slug: "synthetic-composite-roofing",
    title: "Synthetic and Composite Roofing",
    category: "roof-systems",
    description:
      "Premium synthetic and composite roofing options selected for appearance, loading, exposure and manufacturer requirements.",
    customerTypes: ["homeowners", "commercial", "strata"],
    roofSystems: ["synthetic", "composite"],
  },
  {
    slug: "slate-roofing",
    title: "Slate Roofing",
    category: "roof-systems",
    description:
      "Specialty slate roofing assessment and project planning for buildings suited to the material and structural demands.",
    customerTypes: ["homeowners", "commercial", "strata"],
    roofSystems: ["natural slate", "synthetic slate"],
  },
  {
    slug: "tile-roofing",
    title: "Tile Roofing",
    category: "roof-systems",
    description:
      "Tile roof assessment, repair and replacement planning with attention to underlayment, flashings and structural capacity.",
    customerTypes: ["homeowners", "commercial", "strata"],
    roofSystems: ["concrete tile", "clay tile"],
  },
  {
    slug: "roof-waterproofing",
    title: "Roof Waterproofing",
    category: "roof-systems",
    description:
      "Waterproofing systems for roof transitions, decks and vulnerable interfaces selected for the specific assembly and exposure.",
    customerTypes: ["homeowners", "commercial", "strata", "property-managers"],
    roofSystems: ["liquid-applied waterproofing", "sheet membranes"],
    image: "flat",
  },
  {
    slug: "gutter-repair",
    title: "Gutter Repair",
    category: "roofing-components",
    description:
      "Localized repair of leaking, loose or poorly draining gutters when the existing system remains serviceable.",
    customerTypes: ["homeowners", "commercial", "strata"],
    roofSystems: ["gutters and drainage"],
    relatedServiceSlugs: ["gutter-replacement", "gutter-cleaning-downspout-flush"],
  },
  {
    slug: "roof-decking-sheathing-replacement",
    title: "Roof Decking and Sheathing Replacement",
    shortTitle: "Roof Decking Replacement",
    category: "roofing-components",
    description:
      "Replacement of damaged or unsuitable roof decking discovered during investigation or roof replacement work.",
    customerTypes: ["homeowners", "commercial", "strata", "builders"],
    roofSystems: ["wood sheathing", "roof decks"],
  },
  {
    slug: "structural-roof-carpentry-repair",
    title: "Structural Roof Carpentry Repair",
    shortTitle: "Roof Carpentry Repair",
    category: "roofing-components",
    description:
      "Scoped repair of damaged roof framing and carpentry, coordinated with engineering or other specialists where required.",
    customerTypes: ["homeowners", "commercial", "strata", "builders"],
    roofSystems: ["roof framing", "wood structures"],
  },
  {
    slug: "custom-flashing",
    title: "Custom Flashing",
    category: "roofing-components",
    description:
      "Site-measured custom metal flashing for roof transitions, walls, penetrations and other water-shedding details.",
    customerTypes: ["homeowners", "commercial", "strata", "builders"],
    roofSystems: ["sheet metal", "all roof systems"],
    relatedServiceSlugs: ["flashing-roof-penetration-repair"],
  },
  {
    slug: "architectural-sheet-metal",
    title: "Architectural Sheet Metal",
    category: "roofing-components",
    description:
      "Custom architectural metalwork for roof edges, walls and exterior details where durability and finish both matter.",
    customerTypes: ["homeowners", "commercial", "strata", "builders"],
    roofSystems: ["sheet metal", "metal roofing"],
    image: "metal",
    relatedServiceSlugs: ["custom-flashing", "metal-roof-replacement"],
  },
  {
    slug: "snow-guards",
    title: "Snow Guards",
    category: "roofing-components",
    description:
      "Roof-mounted snow guards designed to manage localized snow movement on suitable sloped roofing systems.",
    customerTypes: ["homeowners", "commercial", "strata"],
    roofSystems: ["metal", "shingles", "slate", "tile"],
  },
  {
    slug: "snow-retention-systems",
    title: "Snow Retention Systems",
    category: "roofing-components",
    description:
      "Engineered snow retention planning for roofs where sliding snow and ice require a coordinated system approach.",
    customerTypes: ["homeowners", "commercial", "strata", "property-managers"],
    roofSystems: ["metal", "sloped roofing"],
    relatedServiceSlugs: ["snow-guards"],
  },
  {
    slug: "whole-roof-cedar-cleaning",
    title: "Whole-Roof Cedar Cleaning",
    shortTitle: "Cedar Roof Cleaning",
    category: "cedar",
    description:
      "Whole-roof cedar cleaning using an assessment-led approach intended to protect serviceable shakes and drainage details.",
    customerTypes: ["homeowners", "strata"],
    roofSystems: ["cedar shakes", "cedar shingles"],
    relatedServiceSlugs: ["cedar-shake-repair-restoration"],
  },
  {
    slug: "cedar-roof-treatment",
    title: "Cedar Roof Treatment",
    category: "cedar",
    description:
      "Cedar roof treatment options evaluated after cleaning and condition review, with product suitability confirmed first.",
    customerTypes: ["homeowners", "strata"],
    roofSystems: ["cedar shakes", "cedar shingles"],
    relatedServiceSlugs: ["whole-roof-cedar-cleaning", "cedar-shake-repair-restoration"],
  },
  {
    slug: "cedar-roof-reconditioning",
    title: "Cedar Roof Reconditioning",
    category: "cedar",
    description:
      "A condition-based combination of cleaning, localized shake work and treatment for cedar roofs that remain viable.",
    customerTypes: ["homeowners", "strata"],
    roofSystems: ["cedar shakes", "cedar shingles"],
    relatedServiceSlugs: ["cedar-shake-repair-restoration", "cedar-roof-treatment"],
  },
  {
    slug: "cedar-preventive-maintenance",
    title: "Cedar Preventive Maintenance",
    category: "cedar",
    description:
      "Scheduled cedar roof reviews and maintenance focused on debris, drainage, flashings and early shake deterioration.",
    customerTypes: ["homeowners", "strata", "property-managers"],
    roofSystems: ["cedar shakes", "cedar shingles"],
    relatedServiceSlugs: ["cedar-shake-repair-restoration", "roof-health-check-maintenance"],
  },
  {
    slug: "storm-damage-roofing",
    title: "Storm Damage Roofing",
    category: "storm",
    description:
      "Urgent Lower Mainland stabilization plus planned storm-damage repair and replacement after wind, rain or impact events.",
    customerTypes: ["homeowners", "commercial", "strata", "property-managers"],
    roofSystems: ["all roof systems"],
    emergencyEligible: true,
    plannedEligible: true,
    provinceWideEligible: false,
    relatedServiceSlugs: ["storm-tarping-emergency-dry-in", "emergency-roof-leak-repair"],
  },
  {
    slug: "emergency-storm-assessment",
    title: "Emergency Storm Assessment",
    category: "storm",
    description:
      "Priority Lower Mainland roof assessment after severe weather, subject to safe access and crew availability.",
    customerTypes: ["homeowners", "commercial", "strata", "property-managers"],
    roofSystems: ["all roof systems"],
    emergencyEligible: true,
    plannedEligible: false,
    provinceWideEligible: false,
    relatedServiceSlugs: ["storm-tarping-emergency-dry-in", "storm-damage-roofing"],
  },
  {
    slug: "insurance-damage-documentation-support",
    title: "Insurance Damage Documentation Support",
    shortTitle: "Damage Documentation Support",
    category: "storm",
    description:
      "Roof condition and completed-work documentation that property owners can provide to their insurer or adjuster.",
    customerTypes: ["homeowners", "commercial", "strata", "property-managers"],
    roofSystems: ["all roof systems"],
    emergencyEligible: false,
    plannedEligible: true,
    provinceWideEligible: true,
    relatedServiceSlugs: ["storm-damage-roofing", "roof-health-check-maintenance"],
  },
];
